// Change website animations
const animation = {
  animate: true,
  duration: 750,
  once: false,
};

const header = {
  name: "Sharif Choudhury",
};

const background = {
  // Options: Snow or Particle
  type: "Particle",
};

const section2title = "About Me";

const about = {
  paragraph:
    "I'm a software engineer based in London who loves building things that actually work, whether that's a test framework, a CI/CD pipeline, or a side project at 1am. I graduated from Queen Mary with a First in Computer Science and have spent the last few years at Sky, going from automating test suites to shipping features and defining how teams approach quality. Outside of tech I'm into football, gaming, 3D modelling, and slowly working towards my pilot's licence.",
};

const education = {
  institution: "Queen Mary University of London",
  degree: "BSc (Hons) Computer Science",
  grade: "First Class Honours",
  period: "Sep 2018 to Jun 2021",
};

const certifications = ["ISTQB Certified Tester, Foundation Level"];

const interests = [
  "Football",
  "Gaming",
  "Pilot Training",
  "Tech & Gadgets",
  "3D Modelling",
  "Content Creation",
  "Travel",
];

const skillsBar = [
  { name: "Cypress",        faClass: "fas fa-vial" },
  { name: "Playwright",     faClass: "fas fa-theater-masks" },
  { name: "Pytest",         faClass: "fas fa-bug" },
  { name: "Rest Assured",   faClass: "fas fa-server" },
  { name: "CodeceptJS",     faClass: "fas fa-laptop-code" },
  { name: "Selenium",       faClass: "fas fa-robot" },
  { name: "Python",         faClass: "fab fa-python" },
  { name: "Java",           faClass: "fab fa-java" },
  { name: "JavaScript",     faClass: "fab fa-js" },
  { name: "TypeScript",     faClass: "fas fa-code" },
  { name: "HTML / CSS",     faClass: "fab fa-html5" },
  { name: "SQL",            faClass: "fas fa-database" },
  { name: "AWS",            faClass: "fab fa-aws" },
  { name: "GitLab CI",      faClass: "fab fa-gitlab" },
  { name: "Jenkins",        faClass: "fas fa-cogs" },
  { name: "Git",            faClass: "fab fa-git-alt" },
  { name: "BDD / TDD",      faClass: "fas fa-tasks" },
  { name: "Agile / Scrum",  faClass: "fas fa-sync-alt" },
  { name: "Postman",        faClass: "fas fa-paper-plane" },
  { name: "Jira",           faClass: "fab fa-jira" },
  { name: "Grafana",        faClass: "fas fa-chart-line" },
  { name: "GenAI Tools",    faClass: "fas fa-brain" },
];

// Experience section data
const sectionExpTitle = "Experience";

const experience = [
  {
    role: "Senior QA Engineer",
    company: "Sky",
    location: "London",
    period: "Mar 2025 to Present",
    bullets: [
      "Designed and built a Cypress automation framework from scratch for a Django-based internal platform, implementing Page Object Model architecture.",
      "Integrated automated tests into GitLab CI/CD with parallel execution and nightly regression runs, reducing feedback time and improving release confidence.",
      "Delivered end-to-end test coverage across UI and backend APIs, validating business-critical workflows and complex data relationships.",
      "Developed API test suites using Swagger, Postman, Pytest and Rest Assured, validating response schemas, auth flows, and database state consistency.",
      "Validated data pipelines using AWS Step Functions and DBT models, writing SQL queries in DataGrip to confirm upstream transformations.",
      "Mentored junior QAs and apprentices through pairing sessions, code reviews, and exploratory testing guidance.",
      "Defined QA practices including CI gating, defect triage standards, and test review protocols.",
      "Used GenAI tooling (Claude, ChatGPT) to speed up test authoring, generate test data, and draft edge-case scenarios.",
    ],
  },
  {
    role: "Software Developer",
    company: "Sky",
    location: "London",
    period: "Sep 2024 to Mar 2025",
    bullets: [
      "Developed and maintained subscription-based journeys within the MySky Apps Marketplace, supporting Sky Q and Sky Stream devices.",
      "Improved performance and stability of LightningJS applications through profiling and targeted refactoring.",
      "Implemented frontend regression testing using Cypress (BDD/Gherkin), aligning automated scenarios with business requirements.",
      "Managed builds and deployments via Concourse CI/CD and monitored production performance using Grafana and telemetry tooling.",
    ],
  },
  {
    role: "QA Automation Engineer",
    company: "Sky",
    location: "London",
    period: "Sep 2023 to Sep 2024",
    bullets: [
      "Built automated UI and mobile test suites using Playwright and CodeceptJS across multiple applications.",
      "Integrated automation into Jenkins and AWS-based pipelines for continuous validation and rapid feedback.",
      "Contributed to cross-team release readiness through stable E2E regression coverage and proactive defect identification.",
      "Supported exploratory and edge-case testing for complex user flows.",
    ],
  },
  {
    role: "QA Engineer",
    company: "Solirius Consulting",
    location: "London",
    period: "Sep 2022 to Sep 2023",
    bullets: [
      "Designed and implemented API automation using Rest Assured and Serenity Rest, validating distributed service interactions.",
      "Automated frontend testing with Playwright and CodeceptJS for client-facing web applications.",
      "Participated in Three Amigos and refinement sessions to ensure testability and clear acceptance criteria.",
      "Contributed to QA readiness reports and release sign-off processes.",
    ],
  },
];

const section3Title = "Projects";

const projects = [
  {
    id: "project1",
    name: "Personalised News Application",
    skills: ["A Django-based web application delivering personalised news feeds based on user preferences, integrating news APIs and category filtering."],
    url: "https://i.postimg.cc/NQZRqxFR/News-App-Sharif-Choudhury.png",
  },
  {
    id: "project2",
    name: "Sentiment Analysis Feedback Machine",
    skills: ["A sentiment analysis tool that processes written feedback using Machine Learning and NLP to classify opinions and extract key insights from text data."],
    url: "https://i.postimg.cc/Y22WfnDB/Feedback-Machine-Sharif-Choudhury.png",
  },
  {
    id: "project3",
    name: "NLP Automated Marking System",
    skills: ["Built an NLP-based system for automatically grading written responses, applying Machine Learning, Natural Language Processing, and Sentiment Analysis. Created in ReactJS/JavaScript and Django/Python."],
    url: "https://i.postimg.cc/qpqGdRLH/Full-Marks-Sharif-Choudhury.png",
  },
  {
    id: "project4",
    name: "Catalyst: Unity Zombie Survival Game",
    skills: ["A Unity-based game featuring custom physics systems and AI behaviour, designed and built from scratch including assets, mechanics, and game logic."],
    url: "https://i.postimg.cc/d0K774ZX/Catalyst-Sharif-Choudhury.png",
  },
];

const section4Title = "Digital Design & 3D Modelling";

const miscellaneous = [
  {
    id: "misc1",
    name: "YouTube Thumbnail Design 1",
    skills: ["Design created in Adobe Photoshop. Video edited in Adobe Premiere Pro."],
    url: "https://i.postimg.cc/rp90XxZr/Design1-Sharif-Choudhury.png",
  },
  {
    id: "misc2",
    name: "YouTube Thumbnail Design 2",
    skills: ["Design created in Adobe Photoshop. Video edited in Sony Vegas Pro."],
    url: "https://i.postimg.cc/mDXPhRsZ/Design2-Sharif-Choudhury.png",
  },
  {
    id: "misc3",
    name: "YouTube Thumbnail Design 3",
    skills: ["Design created in Adobe Photoshop. Video edited in Sony Vegas Pro."],
    url: "https://i.postimg.cc/7PRCkx9m/design3sharifchoudhury.png",
  },
  {
    id: "misc4",
    name: "3D Model: Gaming Laptop",
    skills: ["3D model created in Blender. Rendered and composited in Adobe Photoshop."],
    url: "https://i.postimg.cc/3K0KgwDv/Laptop-Model-Sharif-Choudhury.png",
  },
  {
    id: "misc5",
    name: "3D Model: Catalyst Game Sword",
    skills: ["3D game asset modelled in Blender for the Catalyst Unity game project."],
    url: "https://i.postimg.cc/TdrQ14FV/Catalyst-Game-Sw-ord-Sharif-Choudhury.png",
  },
  {
    id: "misc6",
    name: "Video Production Projects",
    skills: ["Multiple video production projects edited with Sony Vegas Pro and Adobe Premiere Pro."],
    url: "https://i.postimg.cc/tyBWkb31/Video-Production-Editing-Sharif-Choudhury.png",
  },
];

const section5Title = "Contact Me";

const contact = {
  pitch: "Feel free to get in touch: sharifchoudhury7@gmail.com",
  copyright: "Sharif Choudhury",
  contactUrl: "",
};

const social = {
  linkedin: "https://www.linkedin.com/in/sharif-choudhury",
  github:   "https://github.com/sharifchoudhury",
};

export {
  animation,
  header,
  background,
  about,
  education,
  certifications,
  interests,
  skillsBar,
  experience,
  projects,
  miscellaneous,
  contact,
  social,
  section2title,
  sectionExpTitle,
  section3Title,
  section4Title,
  section5Title,
};
